// Reusable Review section — used inside product/service detail modal + shop pages.
// Handles: aggregate rating summary, star distribution bars, write-review, edit/delete own, vendor reply.
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Star, ShieldCheck, MessageCircle, Trash2, Edit3, X, Camera, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import Lightbox from '../../components/Lightbox';

export default function ProductReviewSection({ listingId, vendorId, onAggregateChange }) {
  const { user } = useAuth() || {};
  const currentUserId = user?.id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('recent');
  const [showForm, setShowForm] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, images: [], idx: 0 });
  const [editingId, setEditingId] = useState(null);
  const [replyingId, setReplyingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [errText, setErrText] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErrText('');
    try {
      const r = await api.get(`/marketplace/listing/${listingId}/reviews?sort=${sort}`);
      setData(r.data);
      onAggregateChange && onAggregateChange({ avg: r.data.rating_avg, count: r.data.rating_count });
    } catch (e) { setErrText(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [listingId, sort, onAggregateChange]);

  useEffect(() => { load(); }, [load]);

  const isVendor = vendorId && currentUserId && vendorId === currentUserId;
  // Best-effort own-review detection: match by buyer_name (server enforces true ownership at edit/delete)
  const myBuyerName = user?.name || (user?.email || '').split('@')[0];
  const isOwnReview = (r) => currentUserId && r.buyer_name && (r.buyer_name === myBuyerName);
  const canReview = !!currentUserId && !isVendor;

  return (
    <div className="mt-3 rounded-2xl bg-slate-900/60 border border-slate-700 p-3 space-y-3" data-testid="product-reviews-section">
      {/* Summary */}
      <ReviewSummary data={data} loading={loading} />

      {/* Actions row */}
      <div className="flex items-center gap-2 flex-wrap">
        {canReview && !showForm && (
          <button onClick={() => setShowForm(true)} data-testid="reviews-write-cta"
                  className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-yellow-400 to-orange-400 text-slate-900 text-xs font-black flex items-center gap-1">
            <Star size={14} /> Write a Review
          </button>
        )}
        <div className="ml-auto flex items-center gap-1 text-[10px]">
          <span className="opacity-70 mr-1">Sort:</span>
          {[
            { k: 'recent', l: 'Recent' },
            { k: 'helpful', l: 'Helpful' },
            { k: 'high', l: 'High ★' },
            { k: 'low', l: 'Low ★' },
          ].map((s) => (
            <button key={s.k} onClick={() => setSort(s.k)} data-testid={`reviews-sort-${s.k}`}
                    className={`px-2 py-0.5 rounded font-black ${sort === s.k ? 'bg-purple-500 text-white' : 'bg-slate-800 text-slate-300'}`}>
              {s.l}
            </button>
          ))}
        </div>
      </div>

      {/* Write form */}
      {showForm && (
        <ReviewForm listingId={listingId} onClose={() => setShowForm(false)}
                    onDone={() => { setShowForm(false); load(); }} />
      )}

      {errText && <div className="text-xs text-rose-300">{errText}</div>}

      {/* Reviews list */}
      <div className="space-y-2" data-testid="reviews-list">
        {(data?.reviews || []).length === 0 && !loading && (
          <div className="text-center text-xs opacity-60 py-6">
            {canReview ? '📝 Be the first to review!' : 'No reviews yet'}
          </div>
        )}
        {(data?.reviews || []).map((r) => (
          <ReviewCard key={r.id} r={r} isVendor={isVendor}
                      isOwn={isOwnReview(r)}
                      onEdit={() => setEditingId(r.id)}
                      onReply={() => setReplyingId(r.id)}
                      onPhotoClick={(idx) => setLightbox({ open: true, images: r.photos || [], idx })}
                      onChanged={load} />
        ))}
      </div>

      {/* Edit modal */}
      {editingId && (
        <EditReviewModal reviewId={editingId} review={(data?.reviews || []).find((r) => r.id === editingId)}
                         onClose={() => setEditingId(null)} onDone={() => { setEditingId(null); load(); }} />
      )}

      {/* Vendor reply modal */}
      {replyingId && (
        <VendorReplyModal reviewId={replyingId}
                          existingReply={(data?.reviews || []).find((r) => r.id === replyingId)?.vendor_reply}
                          onClose={() => setReplyingId(null)} onDone={() => { setReplyingId(null); load(); }} />
      )}
      {/* Photo lightbox (fullscreen tap-to-zoom) */}
      {lightbox.open && (
        <Lightbox images={lightbox.images} startIndex={lightbox.idx}
                  onClose={() => setLightbox({ open: false, images: [], idx: 0 })} />
      )}
    </div>
  );
}


function ReviewSummary({ data, loading }) {
  const avg = data?.rating_avg || 0;
  const count = data?.rating_count || 0;
  const verified = data?.rating_verified_count || 0;
  const dist = data?.distribution || {};
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="reviews-summary">
      <div className="text-center">
        <div className="text-4xl font-black text-yellow-300">{avg > 0 ? Number(avg).toFixed(1) : '—'}</div>
        <div className="flex justify-center gap-0.5 my-1">
          {[1,2,3,4,5].map((n) => (
            <Star key={n} size={14} className={avg >= n - 0.5 ? 'text-yellow-300 fill-yellow-300' : 'text-slate-600'} />
          ))}
        </div>
        <div className="text-[10px] opacity-70">{count} review{count !== 1 ? 's' : ''} · {verified} verified</div>
      </div>
      <div className="space-y-0.5">
        {[5,4,3,2,1].map((n) => {
          const c = dist[n] || dist[String(n)] || 0;
          const pct = count > 0 ? Math.round((c / count) * 100) : 0;
          return (
            <div key={n} className="flex items-center gap-1 text-[10px]">
              <span className="w-3 text-right font-black">{n}★</span>
              <div className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                <div className="h-full bg-yellow-400" style={{ width: `${pct}%` }} />
              </div>
              <span className="w-6 opacity-70">{c}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function ReviewCard({ r, isVendor, isOwn, onEdit, onReply, onPhotoClick, onChanged }) {
  const [busy, setBusy] = useState(false);
  const del = async () => {
    if (!window.confirm('Delete your review?')) return;
    setBusy(true);
    try { await api.delete(`/marketplace/review/${r.id}`); toast.success('Review deleted'); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl bg-slate-800/50 border border-slate-700 p-2.5 space-y-1.5" data-testid={`review-card-${r.id.slice(0,8)}`}>
      <div className="flex items-start gap-2 flex-wrap">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-xs font-black">
          {(r.buyer_name || '?')[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-xs font-black">{r.buyer_name || 'Anonymous'}</span>
            {r.verified_purchase && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/25 text-emerald-300 border border-emerald-400/60 font-black flex items-center gap-0.5"
                    data-testid={`review-verified-${r.id.slice(0,8)}`}>
                <ShieldCheck size={9} /> Verified
              </span>
            )}
          </div>
          <div className="flex items-center gap-0.5 mt-0.5">
            {[1,2,3,4,5].map((n) => (
              <Star key={n} size={11} className={r.stars >= n ? 'text-yellow-300 fill-yellow-300' : 'text-slate-600'} />
            ))}
            <span className="ml-1 text-[9px] opacity-60">{new Date(r.created_at).toLocaleDateString('en-IN')}</span>
          </div>
        </div>
        {/* Actions */}
        <div className="flex gap-1">
          {isVendor && (
            <button onClick={onReply} data-testid={`review-reply-btn-${r.id.slice(0,8)}`}
                    className="text-[10px] px-2 py-1 rounded bg-cyan-500 text-slate-900 font-black flex items-center gap-1">
              <MessageCircle size={10} /> {r.vendor_reply ? 'Edit Reply' : 'Reply'}
            </button>
          )}
          {isOwn && !isVendor && (
            <>
              <button onClick={onEdit} data-testid={`review-edit-btn-${r.id.slice(0,8)}`} className="text-[10px] p-1 rounded bg-slate-700 hover:bg-slate-600"><Edit3 size={11} /></button>
              <button onClick={del} disabled={busy} data-testid={`review-delete-btn-${r.id.slice(0,8)}`} className="text-[10px] p-1 rounded bg-rose-600 hover:bg-rose-500 disabled:opacity-40"><Trash2 size={11} /></button>
            </>
          )}
        </div>
      </div>
      {r.comment && <div className="text-[11px] opacity-90 leading-relaxed">{r.comment}</div>}
      {r.photos && r.photos.length > 0 && (
        <div className="flex gap-1">
          {r.photos.slice(0, 3).map((p, i) => (
            <img key={i} src={p} alt=""
                 onClick={() => onPhotoClick?.(i)}
                 data-testid={`review-photo-${r.id.slice(0,8)}-${i}`}
                 className="w-14 h-14 rounded object-cover cursor-zoom-in hover:opacity-90 hover:ring-2 hover:ring-yellow-400 transition" />
          ))}
        </div>
      )}
      {r.vendor_reply && (
        <div className="mt-1.5 pl-2 border-l-2 border-cyan-400 bg-slate-900/60 p-1.5 rounded" data-testid={`review-vendor-reply-${r.id.slice(0,8)}`}>
          <div className="text-[9px] font-black text-cyan-300 flex items-center gap-1">
            <MessageCircle size={9} /> Vendor Reply {r.vendor_reply.edited && <span className="opacity-60">(edited)</span>}
          </div>
          <div className="text-[10px] opacity-90 mt-0.5">{r.vendor_reply.text}</div>
        </div>
      )}
    </div>
  );
}


function ReviewForm({ listingId, onClose, onDone }) {
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (stars < 1) { toast.error('Select star rating'); return; }
    setBusy(true);
    try {
      await api.post(`/marketplace/listing/${listingId}/review`, {
        stars, comment: comment || null,
        photos: photos.length > 0 ? photos : null,
      });
      toast.success('⭐ Review submitted');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl bg-slate-800/60 border border-yellow-400/40 p-3 space-y-2" data-testid="reviews-form">
      <div className="flex items-center justify-between">
        <div className="text-xs font-black text-yellow-300">✍️ Write a Review</div>
        <button onClick={onClose} className="opacity-60"><X size={14} /></button>
      </div>
      <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
        {[1,2,3,4,5].map((n) => (
          <button key={n} onClick={() => setStars(n)} onMouseEnter={() => setHover(n)}
                  data-testid={`review-star-${n}`}
                  className="p-0.5 hover:scale-125 transition">
            <Star size={22} className={(hover || stars) >= n ? 'text-yellow-300 fill-yellow-300' : 'text-slate-600'} />
          </button>
        ))}
        {stars > 0 && <span className="ml-2 text-[10px] font-black text-yellow-300">{['','Poor','Fair','Good','Great','Excellent'][stars]}</span>}
      </div>
      <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} maxLength={1000}
                placeholder="Aapki honest feedback…"
                data-testid="review-comment"
                className="w-full px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs" />
      <PhotoUploader photos={photos} setPhotos={setPhotos} />
      <button onClick={submit} disabled={busy || stars < 1} data-testid="review-submit"
              className="w-full py-2 rounded-lg bg-yellow-400 text-slate-900 font-black text-xs disabled:opacity-40">
        {busy ? 'Submitting…' : 'Submit Review'}
      </button>
    </div>
  );
}


function EditReviewModal({ reviewId, review, onClose, onDone }) {
  const [stars, setStars] = useState(review?.stars || 5);
  const [comment, setComment] = useState(review?.comment || '');
  const [photos, setPhotos] = useState(review?.photos || []);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/marketplace/review/${reviewId}/edit`, { stars, comment, photos });
      toast.success('Review updated'); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-3" onClick={onClose} data-testid="edit-review-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-slate-900 border-2 border-yellow-400/60 p-4 space-y-2">
        <div className="text-sm font-black">Edit Review</div>
        <div className="flex items-center gap-1">
          {[1,2,3,4,5].map((n) => (
            <button key={n} onClick={() => setStars(n)} className="p-0.5 hover:scale-125 transition">
              <Star size={22} className={stars >= n ? 'text-yellow-300 fill-yellow-300' : 'text-slate-600'} />
            </button>
          ))}
        </div>
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
                  className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        <PhotoUploader photos={photos} setPhotos={setPhotos} />
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 rounded bg-slate-700 text-xs font-black">Cancel</button>
          <button onClick={submit} disabled={busy} className="flex-1 py-2 rounded bg-yellow-400 text-slate-900 text-xs font-black disabled:opacity-40">Save</button>
        </div>
      </div>
    </div>
  );
}


// Photo uploader — up to 3 photos via /api/upload/image (auto-compressed backend-side)
function PhotoUploader({ photos, setPhotos }) {
  const [uploading, setUploading] = useState(false);
  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 3 - photos.length;
    if (remaining <= 0) { toast.error('Max 3 photos'); return; }
    const toUpload = files.slice(0, remaining);
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of toUpload) {
        const fd = new FormData();
        fd.append('file', f);
        const r = await api.post('/marketplace/review/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (r.data?.url) uploaded.push(r.data.url);
      }
      setPhotos([...photos, ...uploaded]);
      if (uploaded.length) toast.success(`📷 ${uploaded.length} photo(s) added`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  const remove = (idx) => setPhotos(photos.filter((_, i) => i !== idx));
  return (
    <div className="space-y-1.5" data-testid="review-photo-uploader">
      <div className="flex items-center gap-2 flex-wrap">
        {photos.map((p, i) => (
          <div key={i} className="relative group" data-testid={`review-photo-preview-${i}`}>
            <img src={p} alt="" className="w-14 h-14 rounded object-cover border border-slate-600" />
            <button type="button" onClick={() => remove(i)} data-testid={`review-photo-remove-${i}`}
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-rose-600 text-white flex items-center justify-center text-[10px] font-black">×</button>
          </div>
        ))}
        {photos.length < 3 && (
          <label data-testid="review-photo-pick"
                 className="w-14 h-14 rounded border-2 border-dashed border-slate-600 hover:border-yellow-400 flex items-center justify-center cursor-pointer bg-slate-900/50">
            {uploading ? <Loader2 size={16} className="animate-spin text-yellow-300" /> : <Camera size={16} className="text-slate-400" />}
            <input type="file" accept="image/*" multiple onChange={onPick} className="hidden" disabled={uploading} />
          </label>
        )}
      </div>
      <div className="text-[9px] opacity-60">📷 {photos.length}/3 photos · Optional, auto-compressed</div>
    </div>
  );
}


function VendorReplyModal({ reviewId, existingReply, onClose, onDone }) {
  const [text, setText] = useState(existingReply?.text || '');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim()) return toast.error('Reply khali nahi');
    setBusy(true);
    try {
      await api.post(`/marketplace/review/${reviewId}/reply`, { text });
      toast.success('Reply posted'); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm('Delete reply?')) return;
    setBusy(true);
    try { await api.delete(`/marketplace/review/${reviewId}/reply`); toast.success('Deleted'); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-3" onClick={onClose} data-testid="vendor-reply-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-slate-900 border-2 border-cyan-400/60 p-4 space-y-2">
        <div className="text-sm font-black text-cyan-300 flex items-center gap-1"><MessageCircle size={14} /> Vendor Reply</div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={800}
                  placeholder="Buyer ko dhanyawad karein / clarify karein…"
                  data-testid="vendor-reply-text"
                  className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        <div className="flex gap-2">
          {existingReply && (
            <button onClick={del} disabled={busy} data-testid="vendor-reply-delete"
                    className="px-3 py-2 rounded bg-rose-600 text-white text-xs font-black disabled:opacity-40">Delete</button>
          )}
          <button onClick={onClose} className="flex-1 py-2 rounded bg-slate-700 text-xs font-black">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="vendor-reply-submit"
                  className="flex-1 py-2 rounded bg-cyan-500 text-slate-900 text-xs font-black disabled:opacity-40">
            {existingReply ? 'Update' : 'Post'} Reply
          </button>
        </div>
      </div>
    </div>
  );
}
